import { loadMessages, locale } from 'devextreme/localization';
import * as localization from 'devextreme/localization';
import enMessages from 'devextreme/localization/messages/en.json';
import { _t } from '@smart-builder/util';

declare global {
  interface Window {
    SB_DEVEX_loadDateMessages: (_t: (message: string) => string) => void;
  }
}

// skip any keys that start with the following
const skipPrefixes = [
  'validation',
  'dxScrollView',
  'dxRangeSlider',
  'dxSwitch',
  'dxForm',
  'dxPager',
  'dxPivotGrid',
  'dxScheduler',
  'dxCalendar',
  'dxTagBox',
  'vizExport',
  'dxHtmlEditor',
  'dxFileManager',
  'dxDiagram',
  'dxGantt',
];

function shouldTranslate(key: string) {
  for (let prefix of skipPrefixes) {
    if (key.startsWith(prefix + '-')) {
      return false;
    }
  }
  return true;
}

type Placeholder = '{0}' | '%1%';

function incrementBy(placeHolder: Placeholder, amount: number): string {
  const [start, middle, finish] = placeHolder;
  return start + (parseInt(middle) + amount).toString() + finish;
}

function convertPlaceholders(
  str: string,
  from: '{0}' | '%1%',
  to: '{0}' | '%1%',
) {
  // {0}, {1}, {2} corresponds to %1%, %2%, %3% respectively.
  let result = str;
  for (let i = 0; i < 9; i++) {
    result = result.replaceAll(incrementBy(from, i), incrementBy(to, i));
  }
  return result;
}

function doTranslate(key: string, origValue: string) {
  if (!shouldTranslate(key)) return origValue;

  // DevEx uses {N} syntax, we use %N% syntax - need to convert back and forth
  const valueToTranslate = convertPlaceholders(origValue, '{0}', '%1%');
  return convertPlaceholders(_t(valueToTranslate), '%1%', '{0}');
}

let translationsRegistered = false;
export function registerDevExTranslations() {
  if (translationsRegistered) {
    // no need to do this more than once
    return;
  }

  // we do the translation ourselves, and override the english dictionary with our translated one
  const translated = Object.fromEntries(
    Object.entries(enMessages.en).map(([k, v]) => [k, doTranslate(k, v)]),
  );
  loadMessages({ en: translated });
  locale('en');

  // T12239 SS 20230725 Translate month, day, period and quarter messages.
  // Note: These messages are located separately to the normal messages used by DevEx and are translated via the function
  // SB_DEVEX_loadDateMessages which is patched into esm\localization\default_date_names.js
  window.SB_DEVEX_loadDateMessages(_t);

  // SS 20230724 Also need to ensure DevEx's Intl implementation is disabled as we also want to be able to
  // handle controlling decimal & thousands separators ourselves. If disableIntl is not called then the
  // decimalSeparator and thousandsSeparator settings on the DevEx global config will be ignored. The DevEx config
  // is checked and updated if necessary when a control first renders, see ensureDevExFormatConfigUpdated.
  (localization as any).disableIntl();
}

// This is for extracting all relevant strings to include in U_ReactStrings.pas
// call logAllDevExStrings from F12 inspector window
(window as any).logAllDevExStrings = () => {
  const strings = Object.entries(enMessages.en)
    .filter(([k, _]) => shouldTranslate(k))
    .map(([_, v]) => convertPlaceholders(v, '{0}', '%1%'));
  const uniqStrings = [...new Set(strings)].sort(
    (a, b) => a.toLowerCase().localeCompare(b.toLowerCase()), // ignore case
  );

  const delphiCodeFragment = `cReactStringsDevEx: TArray<String> = [
${uniqStrings.map(s => `    '${s}'`).join(',\n')}
  ];`;

  console.info(delphiCodeFragment);
};
